import React, {useState, useMemo, useRef} from 'react'
import {connect} from 'react-redux'
import {bindActionCreators} from 'redux'
import styled from 'styled-components'

import { CitiesUSA } from '../../EditJobCard/constants'
import { 
    SettingTypes,
    EmploymentTypes,
    PositionTypes,
    ExperienceLevels,
    ExperienceYears,
    Languages,
    Skills,
    VisaSponsorshipOptions,
} from '../../EditJobCard'
import { getSavedFilters } from '../../../../../redux/job'
import { 
    getFiltersCount, 
    getSelectedFilter,
    formatCurrency
} from './utils'
import { 
    InitialJobFilters, 
    DatePostedOptions,
    DatePostedOptionsDict,
    SecurityClearanceOptions,
    SecurityClearanceOptionsDict,
} from '../../JobsFeed'
import { getIsCandidatePremiumUser } from '../../../../../redux/user'
import { ModalTypes } from '../../../../../containers/ModalProvider'
import { addModal, removeModal } from '../../../../../redux/modal'
import { SubscriptionTiersFormatted } from '../../../../../redux/user'

import { Confirm } from '../../../modals/Confirm'
import { SearchableSelectableInput } from '../../../common/SearchableSelectableInput'
import { Pill } from '../../../common/Pill'
import { FilterRow } from '../../FilterRow'
import { PillLabel } from '../../../common/PillLabel'
import { Button } from '../../../common/Button'
import { Tooltip } from '../../../common/Tooltip'

export const FreeTierMaxFilterCount = 20

export const JobFiltersModalComponent = props => {
    const {
        modalID,
        initialFilters, // {filterKey: []}
        
        onClickApply, // (onSuccess, onFailure, filters) => void
        onClickDeleteFilter, // (filterID, onDeleteSuccess) => void
        navigateToPremiumPage, // () => void
    } = props

    // State

    const containerRef = useRef(null)
    const [filters, setFilters] = useState(initialFilters)
    const [formData, setFormData] = useState({
        setting: '',
        location: '',
        includedSkill: '',
        excludedSkill: '',
        includedLanguage: '',
        exlcudedLanguage: '',
    })
    const selectedSavedFilterID = useMemo(() => {
        const selectedFilter = getSelectedFilter(filters, props.savedFilters)
        return selectedFilter ? selectedFilter._id : undefined
    }, [filters, props.savedFilters])

    // Utils

    function prettyPrintObject(obj, level = 0) {
        const indent = ' '.repeat(level * 4)
        const nextIndent = ' '.repeat((level + 1) * 4)
    
        if (Array.isArray(obj)) {
            const isScalar = obj.every(v => typeof v !== 'object' || v === null)
    
            if (isScalar) {
                return `[${obj.map(v => JSON.stringify(v)).join(', ')}]`
            }
    
            const items = obj.map(v => `${nextIndent}${prettyPrintObject(v, level + 1)}`)
            return `[\n${items.join(',\n')}\n${indent}]`
        }
    
        if (typeof obj === 'object' && obj !== null) {
            const entries = Object.entries(obj).map(
                ([key, val]) => `${nextIndent}"${key}": ${prettyPrintObject(val, level + 1)}`
            )
            return `{\n${entries.join(',\n')}\n${indent}}`
        }
    
        return JSON.stringify(obj)
    }
    
    const getSelectionText = filterName => {
        switch (filterName) {
            case 'datePosted':
                if (filters.datePosted === 'anytime') return 'any'
                else return DatePostedOptionsDict[filters.datePosted]
            case 'requiresClearance':
                if (filters.requiresClearance === 'any') return 'any'
                else return SecurityClearanceOptionsDict[filters.requiresClearance]
            case 'settings':
            case 'locations':
            case 'employmentTypes':
            case 'positions':
            case 'experienceLevels':
            case 'experienceYears':
            case 'includedLanguages':
            case 'includedSkills':
            case 'sponsorsVisa':
                return filters[filterName].length ? `${filters[filterName].length} selected` : 'any'
            case 'excludedLanguages':
            case 'excludedSkills':
                return filters[filterName].length ? `${filters[filterName].length} selected` : 'none'
            case 'salaryMin':
                return filters.salaryMin == '0' ? 'none' : `$ ${formatCurrency(filters.salaryMin)}`
            default:
                break
        }
    }

    const showCantAddMoreFiltersModal = () => {
        props.addModal(ModalTypes.CONFIRM, {
            title: 'Reached filter count limit',
            message: `You are limited to 3 filters at a time on the free plan. Upgrade to ${SubscriptionTiersFormatted.candidatePremium} to apply unlimited filters.`,
            confirmButtonTitle: 'Go premium',
            onConfirm: onSuccess => {
                navigateToPremiumPage()
                props.removeModal(modalID)
                onSuccess()
            }
        })
    }

    // Direct

    const onChangeField = e => {
        const {name, value} = e.target
        
        setFormData(curr => ({
            ...curr,
            [name]: value
        }))
    }

    const onClickOption = (option, fieldName) => {
        let filterName
        if (fieldName === 'sponsorsVisa') filterName = fieldName
        else filterName = fieldName + 's'

        setFilters( curr => {
            const updatedFilters = {
                ...curr,
                [filterName]: curr[filterName].includes(option) ?
                    curr[filterName].filter(item => item !== option)
                    : [...curr[filterName], option]
                ,
            }
            const updatedFilterCount = getFiltersCount(updatedFilters)

            if (updatedFilterCount <= FreeTierMaxFilterCount || props.isCandidatePremiumUser) {
                return updatedFilters
            } else {
                showCantAddMoreFiltersModal()
                return curr
            }
        })
    }

    const onClickDatePostedOption = (option) => {
        setFilters(curr => {
            const updatedFilters = {
                ...curr,
                datePosted: curr.datePosted === option ? 'anytime' : option
            }

            const updatedFilterCount = getFiltersCount(updatedFilters)

            if (updatedFilterCount <= FreeTierMaxFilterCount || props.isCandidatePremiumUser) {
                return updatedFilters
            } else {
                showCantAddMoreFiltersModal()
                return curr
            }
        })
    }

    const onClickRequiresClearanceOption = (option) => {
        setFilters(curr => {
            const updatedFilters = {
                ...curr,
                requiresClearance: curr.requiresClearance === option ? 'any' : option
            }

            const updatedFilterCount = getFiltersCount(updatedFilters)

            if (updatedFilterCount <= FreeTierMaxFilterCount || props.isCandidatePremiumUser) {
                return updatedFilters
            } else {
                showCantAddMoreFiltersModal()
                return curr
            }
        })
    }

    const onClickClearFilter = (filterName) => {
        switch(filterName) {
            case 'datePosted':
                setFilters(curr => ({
                    ...curr,
                    datePosted: 'anytime',
                }))
                break
            case 'requiresClearance':
                setFilters(curr => ({
                    ...curr,
                    requiresClearance: 'any'
                }))
                break
            case 'settings':
            case 'locations':
            case 'employmentTypes':
            case 'positions':
            case 'experienceLevels':
            case 'experienceYears':
            case 'includedLanguages':
            case 'excludedLanguages':
            case 'includedSkills':
            case 'excludedSkills':
            case 'sponsorsVisa':
                setFilters(curr => ({
                    ...curr,
                    [filterName]: []
                }))
                break
            case 'salaryMin':
                setFilters(curr => ({
                    ...curr,
                    salaryMin: InitialJobFilters.salaryMin
                }))
                break
            default:
                break
        }
    }

    const onClickApplySavedFilter = (filterID) => {
        const savedFilter = props.savedFilters.find(filter => filter._id === filterID)
        setFilters(savedFilter)
    }

    const onClickClearFilters = () => {
        setFilters(InitialJobFilters)
    }

    const onClickGoPremium = () => {
        props.removeModal(modalID)
        navigateToPremiumPage()
    }

    const onChangeSalary = e => {
        const {name, value} = e.target

        if (value.length === 0) {
            setFilters(curr => ({
                ...curr,
                salaryMin: '0'
            }))
            return
        }

        const chars = [...value]
        let validatedValue = ''
        for (let i = 0; i < chars.length; i++) {
            const char = chars[i]
            if (!isNaN(parseInt(char, 10))) {
                validatedValue += char
            }
        }

        const validatedNumber = Number(validatedValue).toString()

        setFilters(curr => ({
            ...curr,
            salaryMin: validatedNumber
        }))
    }

    // Render

    return (
        <Confirm
            title='Job Filters'
            confirmButtonTitle='Apply filters'
            onConfirm={onClickApply}
            modalID={modalID}
            onConfirmExtraArg={{updatedFilters: filters, savedFilterID: selectedSavedFilterID}}
        >
            <Root ref={containerRef}>
                <div className='saved-filters-section-header'>
                    <h4 className='saved-filters-section-title'>Saved filters</h4>
                    {props.isCandidatePremiumUser ? null :
                        <PillLabel
                            title={`${SubscriptionTiersFormatted.candidatePremium} feature`}
                            color='yellow'
                            size='m'
                            style={{marginLeft: 10}}
                        />
                    }
                </div>
                {props.savedFilters.length ? 
                    props.savedFilters.map(({title, _id, ...filter}) => (
                        <FilterRow
                            title={title}
                            selectionText={''}
                            actionButtonTitle='Apply'
                            dangerButtonTitle='Delete'
                            onlyShowButtonsOnHover={true}
                            onClickActionButton={() => onClickApplySavedFilter(_id)}
                            onClickDangerButton={() => onClickDeleteFilter(_id)}
                            titleRightChild={_id === selectedSavedFilterID ?
                                <PillLabel
                                    title='Active'
                                    color='green'
                                    size='s'
                                    style={{marginLeft: 10, marginRight: 10}}
                                />
                                : null
                            }
                            isExpandable={true}
                            key={_id}
                        >
                            <pre>Filter: {prettyPrintObject(filter.asMongoFilter)}</pre>
                        </FilterRow>
                    ))
                    : <div className='no-saved-filters-container'>
                        <p>You have no saved filters</p>
                    </div>
                }
                {props.isCandidatePremiumUser ? null :
                    <Button 
                        title='Go premium'
                        priority={3}
                        type='clear'
                        onClick={onClickGoPremium}
                        style={{alignSelf: 'center', marginTop: 10}}
                    />
                }
                <div className='section-header'>
                    <h4 className='section-title' style={{marginTop: 25}}>Custom filters</h4>
                    <Button
                        title='Clear filters'
                        onClick={onClickClearFilters}
                        type='danger'
                        priority={3}
                        style={{marginRight: 15}}
                    />
                </div>
                <FilterRow
                    title='Date posted'
                    filterName='datePosted'
                    selectionText={getSelectionText('datePosted')}
                    filterActive={filters.datePosted !== 'anytime'}
                    onClickActionButton={() => onClickClearFilter('datePosted')}

                >
                    <div className='pills-row row'>
                        {DatePostedOptions.map(({id, title}) => (
                            <Pill
                                title={title}
                                active={filters.datePosted === id}
                                id={id}
                                onClick={onClickDatePostedOption}
                                className='pill-option'
                            />
                        ))}
                    </div>
                </FilterRow>
                <FilterRow
                    title='Settings'
                    filterName='settings'
                    selectionText={getSelectionText('settings')}
                    filterActive={filters.settings.length > 0}
                    onClickActionButton={() => onClickClearFilter('settings')}

                >
                    <div className='pills-row row'>
                        {SettingTypes.map(({id, title}) => (
                            <Pill
                                title={title}
                                active={filters.settings.includes(id)}
                                id={id}
                                onClick={onClickOption}
                                fieldName='setting'
                                className='pill-option'
                            />
                        ))}
                    </div>
                </FilterRow>
                <FilterRow
                    title='Locations'
                    filterName='locations'
                    selectionText={getSelectionText('locations')}
                    filterActive={filters.locations.length > 0}
                    onClickActionButton={() => onClickClearFilter('locations')}
                >
                    <SearchableSelectableInput
                        options={CitiesUSA}
                        selectedOptions={filters.locations}
                        value={formData.location}
                        fieldName='location'
                        onChange={onChangeField}
                        onClickOption={onClickOption}
                        className='row'
                    />
                </FilterRow>
                <FilterRow
                    title='Employment types'
                    filterName='employmentTypes'
                    selectionText={getSelectionText('employmentTypes')}
                    filterActive={filters.employmentTypes.length > 0}
                    onClickActionButton={() => onClickClearFilter('employmentTypes')}
                >
                    <div className='pills-row row'>
                        {EmploymentTypes.map(({id, title}) => (
                            <Pill
                                title={title}
                                active={filters.employmentTypes.includes(id)}
                                id={id}
                                onClick={onClickOption}
                                fieldName='employmentType'
                                className='pill-option'
                            />
                        ))}
                    </div>
                </FilterRow>
                <FilterRow
                    title='Position types'
                    filterName='positions'
                    selectionText={getSelectionText('positions')}
                    onClickActionButton={() => onClickClearFilter('positions')}
                    filterActive={filters.positions.length > 0}
                >
                    <div className='pills-row row'>
                        {PositionTypes.map(({id, title}) => (
                            <Pill
                                title={title}
                                active={filters.positions.includes(id)}
                                id={id}
                                onClick={onClickOption}
                                fieldName='position'
                                className='pill-option'
                            />
                        ))}
                    </div>
                </FilterRow>
                <FilterRow
                    title='Experience levels'
                    filterName='experienceLevels'
                    selectionText={getSelectionText('experienceLevels')}
                    filterActive={filters.experienceLevels.length > 0}
                    onClickActionButton={() => onClickClearFilter('experienceLevels')}
                >
                    <div className='pills-row row'>
                        {ExperienceLevels.map(({id, title}) => (
                            <Pill
                                title={title}
                                active={filters.experienceLevels.includes(id)}
                                id={id}
                                onClick={onClickOption}
                                fieldName='experienceLevel'
                                className='pill-option'
                            />
                        ))}
                    </div>
                </FilterRow>
                <FilterRow
                    title='Years of experience'
                    filterName='experienceYears'
                    selectionText={getSelectionText('experienceYears')}
                    filterActive={filters.experienceYears.length > 0}
                    onClickActionButton={() => onClickClearFilter('experienceYears')}
                >
                    <div className='pills-row row'>
                        {ExperienceYears.map(({id, title}) => (
                            <Pill
                                title={title}
                                active={filters.experienceYears.includes(id)}
                                id={id}
                                onClick={onClickOption}
                                fieldName='experienceYear'
                                className='pill-option'
                            />
                        ))}
                    </div>
                </FilterRow>
                <FilterRow
                    title='Included langauges'
                    filterName='includedLanguages'
                    selectionText={getSelectionText('includedLanguages')}
                    filterActive={filters.includedLanguages.length > 0}
                    onClickActionButton={() => onClickClearFilter('includedLanguages')}
                    titleRightChild={
                        <Tooltip title={`Jobs must include at least one`}>
                            <i className='bi-question-circle help-icon' />
                        </Tooltip>
                    }
                >
                    <SearchableSelectableInput
                        options={Languages}
                        selectedOptions={filters.includedLanguages}
                        value={formData.includedLanguage}
                        fieldName='includedLanguage'
                        onChange={onChangeField}
                        onClickOption={onClickOption}
                        className='row'
                    />
                </FilterRow>
                <FilterRow
                    title='Excluded langauges'
                    filterName='excludedLanguages'
                    selectionText={getSelectionText('excludedLanguages')}
                    filterActive={filters.excludedLanguages.length > 0}
                    onClickActionButton={() => onClickClearFilter('excludedLanguages')}
                >
                    <SearchableSelectableInput
                        options={Languages}
                        selectedOptions={filters.excludedLanguages}
                        value={formData.excludedLanguage}
                        fieldName='excludedLanguage'
                        onChange={onChangeField}
                        onClickOption={onClickOption}
                        className='row'
                    />
                </FilterRow>
                <FilterRow
                    title='Included skills'
                    filterName='includedSkills'
                    selectionText={getSelectionText('includedSkills')}
                    filterActive={filters.includedSkills.length > 0}
                    onClickActionButton={() => onClickClearFilter('includedSkills')}
                    titleRightChild={
                        <Tooltip title={`Jobs must include at least one`}>
                            <i className='bi-question-circle help-icon' />
                        </Tooltip>
                    }
                >
                    <SearchableSelectableInput
                        options={Skills}
                        selectedOptions={filters.includedSkills}
                        value={formData.includedSkill}
                        fieldName='includedSkill'
                        onChange={onChangeField}
                        onClickOption={onClickOption}
                        className='row'
                    />
                </FilterRow>
                <FilterRow
                    title='Excluded skills'
                    filterName='excludedSkills'
                    selectionText={getSelectionText('excludedSkills')}
                    filterActive={filters.excludedSkills.length > 0}
                    onClickActionButton={() => onClickClearFilter('excludedSkills')}
                >
                    <SearchableSelectableInput
                        options={Skills}
                        selectedOptions={filters.excludedSkills}
                        value={formData.excludedSkill}
                        fieldName='excludedSkill'
                        onChange={onChangeField}
                        onClickOption={onClickOption}
                        className='row'
                    />
                </FilterRow>
                <FilterRow
                    title='Minium yearly salary (USD)'
                    filterName='salaryMin'
                    selectionText={getSelectionText('salaryMin')}
                    filterActive={![0, '0'].includes(filters.salaryMin)}
                    onClickActionButton={() => onClickClearFilter('salaryMin')}
                >
                    <div className='salary-min-container'>
                        <input
                            className='salary-input-container'
                            value={filters.salaryMin}
                            onChange={onChangeSalary}
                        />
                        <p style={{marginLeft: 10}}><strong>per year</strong></p>
                    </div>
                </FilterRow>
                <FilterRow
                    title='Sponsors visa'
                    filterName='sponsorsVisa'
                    selectionText={getSelectionText('sponsorsVisa')}
                    filterActive={filters.sponsorsVisa.length > 0}
                    onClickActionButton={() => onClickClearFilter('sponsorsVisa')}

                >
                    <div className='pills-row row'>
                        {VisaSponsorshipOptions.map(({id, title}) => (
                            <Pill
                                title={title}
                                active={filters.sponsorsVisa.includes(id)}
                                id={id}
                                onClick={onClickOption}
                                fieldName='sponsorsVisa'
                                className='pill-option'
                            />
                        ))}
                    </div>
                </FilterRow>
                <FilterRow
                    title='Security clearance'
                    filterName='requiresClearance'
                    selectionText={getSelectionText('requiresClearance')}
                    filterActive={filters.requiresClearance !== 'any'}
                    onClickActionButton={() => onClickClearFilter('requiresClearance')}
                >
                    <div className='pills-row row'>
                        {SecurityClearanceOptions.map(({id, title}) => (
                            <Pill
                                title={title}
                                active={filters.requiresClearance === id}
                                id={id}
                                onClick={onClickRequiresClearanceOption}
                                className='pill-option'
                            />
                        ))}
                    </div>
                </FilterRow>
            </Root>
        </Confirm>
    )
}

const Root = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    border: 1px solid ${p => p.theme.bc};
    border-radius: 15px;
    padding-top: 15px;

    & .saved-filters-section-header {
        margin-bottom: 20px;
        margin-left: 15px;
        display: flex;
        justify-content: flex-start;
        align-items: center;
    }

    & .saved-filters-section-title {
        color: ${p => p.theme.tint};
    }

    & .section-title {
        margin-bottom: 20px;
        margin-left: 15px;
        color: ${p => p.theme.tint};
    }
    & .no-saved-filters-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-around;
    }

    & .section-header {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        margin-top: 15px;
    }

    & .salary-min-container {
        display: flex;
        justify-content: flex-start;
        align-items: center;
    }

    & .help-icon {
        font-size: 17px;
        color: ${p => p.theme.textSecondary};
        margin-left: 10px;
    }
`

const mapStateToProps = state => ({
    savedFilters: getSavedFilters(state),
    isCandidatePremiumUser: getIsCandidatePremiumUser(state),
})

const mapDispatchToProps = dispatch => bindActionCreators({
    addModal,
    removeModal,
}, dispatch)

export const JobFiltersModal = connect(mapStateToProps, mapDispatchToProps)(JobFiltersModalComponent)